import React, { useEffect, useState } from 'react';
import { useNotify, useTranslate } from 'ra-core';
import { useNavigate, Link } from 'react-router-dom';
import { authProvider } from '../../../authProvider';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

type Props = {
  oobCode: string;
};

const ResetPasswordPage = ({ oobCode }: Props) => {
  const [isCodeValid, setIsCodeValid] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const navigate = useNavigate();
  const notify = useNotify();
  const t = useTranslate();

  useEffect(() => {
    authProvider
      .verifyPasswordResetCode(oobCode)
      .then(() => setIsCodeValid(true))
      .catch(err => {
        console.error('invalid code: ', err);
        setIsCodeValid(false);
      });
  }, [oobCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      notify(t('auth.error.password_mismatch'), { type: 'error' });
      return;
    }
    try {
      await authProvider.confirmPasswordReset(oobCode, newPassword);
      notify(t('auth.success.password_reset'), { type: 'success' });
      navigate('/login');
    } catch (error: any) {
      console.error(error);
      notify(t('auth.error.reset_password_failed'), { type: 'error' });
    }
  };

  if (!isCodeValid) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="mb-4">{t('auth.error.invalid_link')}</p>
          <Button asChild>
            <Link to="/forgot-password">{t('auth.button.retry')}</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div>
        <Link to="/">
          <img
            alt="logo"
            src="https://storage.googleapis.com/public-goloot-production/goloot/goloot-logo-white.png"
            style={{ height: 55, width: 'auto', margin: 'auto' }}
          />
        </Link>
      </div>
      <Card className="shadow-elev-3">
        <CardHeader>
          <CardTitle className="text-xl">
            {t('auth.message.reset_password.set_new_password')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <div className="flex flex-col gap-3">
              <Input
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder={t('auth.label.new_password')}
                required
              />
              <Input
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder={t('auth.label.confirm_password')}
                required
              />
              <Button type="submit">{t('auth.button.update_password')}</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </>
  );
};

export default ResetPasswordPage;
